import React from 'react';
import { Alert, Button, Chip } from '@mui/material';
import { MdSupportAgent, MdMarkEmailUnread, MdAutorenew, MdCheckCircle, MdTimer, MdSpeed, MdHourglassBottom, MdReceiptLong } from 'react-icons/md';
import TabbedReport, { pill, TwoLine, Dash, shareBar } from './TabbedReport';
import { fmtDate } from './reportShared';

const STATUS = { open: ['Open', '#fef3c7', '#92400e'], 'in-progress': ['In progress', '#e0f2fe', '#075985'], resolved: ['Resolved', '#dcfce7', '#166534'] };
const statusChip = (s) => {
  const [text, bg, fg] = STATUS[s] || [s, '#f1f5f9', '#475569'];
  return <Chip size="small" label={text} sx={pill(bg, fg)} />;
};
// Hours → "45 min" / "6.5 h" / "2.1 days"
const dur = (h) => (h === null || h === undefined ? null : h < 1 ? `${Math.round(h * 60)} min` : h < 48 ? `${Math.round(h * 10) / 10} h` : `${Math.round((h / 24) * 10) / 10} days`);
const durCell = (h, warnAt = 24) => {
  if (h === null || h === undefined) return <Dash />;
  const [bg, fg] = h <= warnAt / 4 ? ['#dcfce7', '#166534'] : h <= warnAt ? ['#fef3c7', '#92400e'] : ['#fee2e2', '#b91c1c'];
  return <Chip size="small" label={dur(h)} sx={pill(bg, fg)} />;
};
const customerCol = { key: 'customer', label: 'Customer', text: (r) => r.customer, cell: (r) => <TwoLine top={r.customer} bottom={r.phone} />, extra: (r) => ({ Phone: r.phone }) };
const ticketCols = [
  { key: 'referenceNo', label: 'Ticket', bold: true, text: (r) => r.referenceNo },
  { key: 'subject', label: 'Subject', text: (r) => r.subject, cell: (r) => <TwoLine top={r.subject} bottom={r.orderNumber ? `Order ${r.orderNumber}` : 'General'} />, extra: (r) => ({ 'Order No.': r.orderNumber }) },
  customerCol,
  { key: 'createdAt', label: 'Raised', text: (r) => fmtDate(r.createdAt) },
  { key: 'status', label: 'Status', text: (r) => STATUS[r.status]?.[0], cell: (r) => statusChip(r.status) },
];

const tabs = [
  {
    key: 'tickets',
    label: 'All Tickets',
    defaultSort: 'date_desc',
    sorts: [['date_desc', 'Newest first'], ['age_desc', 'Oldest first'], ['response_desc', 'Slowest first response']],
    rowAlert: (r) => r.waitingOn === 'staff' && r.waitingHours > 24,
    columns: [
      ...ticketCols,
      { key: 'firstResponseHours', label: 'First Response', center: true, text: (r) => dur(r.firstResponseHours) || 'No reply yet', cell: (r) => (r.firstResponseHours === null ? (r.status === 'resolved' ? <Dash /> : <Chip size="small" label="No reply yet" sx={pill('#fee2e2', '#b91c1c')} />) : durCell(r.firstResponseHours)) },
      { key: 'resolutionHours', label: 'Resolved In', center: true, text: (r) => dur(r.resolutionHours) || '', cell: (r) => durCell(r.resolutionHours, 72) },
      { key: 'messages', label: 'Messages', numeric: true, text: (r) => r.messages, extra: (r) => ({ 'Staff Replies': r.staffReplies }) },
      { key: 'waitingOn', label: 'Waiting On', center: true, text: (r) => r.waitingOn || '', cell: (r) => (r.waitingOn === 'staff' ? <Chip size="small" label={`You · ${dur(r.waitingHours)}`} sx={r.waitingHours > 24 ? pill('#fee2e2', '#b91c1c') : pill('#fef3c7', '#92400e')} /> : r.waitingOn === 'customer' ? <Chip size="small" label="Customer" sx={pill('#e0f2fe', '#075985')} /> : <Dash />) },
    ],
  },
  {
    key: 'waiting',
    label: 'Waiting on You',
    emptyText: "You're all caught up — no tickets are waiting on a reply.",
    rowAlert: (r) => r.waitingHours > 24,
    columns: [
      ...ticketCols,
      { key: 'waitingHours', label: 'Waiting For', center: true, text: (r) => dur(r.waitingHours), cell: (r) => durCell(r.waitingHours) },
      { key: 'staffReplies', label: 'Staff Replies So Far', numeric: true, text: (r) => r.staffReplies },
      { key: 'ageHours', label: 'Ticket Age', center: true, text: (r) => dur(r.ageHours) },
    ],
  },
  {
    key: 'subjects',
    label: 'By Subject',
    columns: [
      { key: 'subject', label: 'Subject', bold: true, text: (r) => r.subject },
      { key: 'tickets', label: 'Tickets', numeric: true, bold: true, text: (r) => r.tickets },
      { key: 'sharePct', label: '% of Tickets', text: (r) => `${r.sharePct}%`, cell: (r) => shareBar(r.sharePct, 'linear-gradient(90deg,#f43f5e,#dc2626)') },
      { key: 'open', label: 'Still Open', numeric: true, text: (r) => r.open, cell: (r) => (r.open ? <Chip size="small" label={r.open} sx={pill('#fef3c7', '#92400e')} /> : 0) },
      { key: 'resolved', label: 'Resolved', numeric: true, text: (r) => r.resolved },
      { key: 'orderRelated', label: 'About an Order', numeric: true, text: (r) => r.orderRelated },
      { key: 'avgFirstResponseHours', label: 'Avg First Response', center: true, text: (r) => dur(r.avgFirstResponseHours) || '', cell: (r) => durCell(r.avgFirstResponseHours) },
      { key: 'avgResolutionHours', label: 'Avg Resolution', center: true, text: (r) => dur(r.avgResolutionHours) || '', cell: (r) => durCell(r.avgResolutionHours, 72) },
    ],
  },
  {
    key: 'customers',
    label: 'By Customer',
    rowAlert: (r) => r.tickets >= 3,
    columns: [
      customerCol,
      { key: 'tickets', label: 'Tickets', numeric: true, bold: true, text: (r) => r.tickets, cell: (r) => <Chip size="small" label={r.tickets} sx={r.tickets >= 3 ? pill('#fee2e2', '#b91c1c') : r.tickets === 2 ? pill('#fef3c7', '#92400e') : pill('#f1f5f9', '#475569')} /> },
      { key: 'open', label: 'Open', numeric: true, text: (r) => r.open },
      { key: 'lastTicket', label: 'Last Ticket', text: (r) => fmtDate(r.lastTicket) },
      { key: 'subjects', label: 'Subjects', text: (r) => r.subjects },
    ],
  },
];

const SupportReport = () => (
  <TabbedReport
    route="reports/support-report"
    title="Support Report"
    subtitle="Ticket volume, how fast your team responds and resolves, and what customers are asking about"
    filePrefix="Support"
    tabs={tabs}
    searchLabel="Search ticket / subject / customer / order"
    filters={[{ key: 'status', label: 'Status', tabs: ['tickets'], options: Object.entries(STATUS).map(([value, v]) => ({ value, label: v[0] })) }]}
    cards={(s, { goTo, isActive }) => [
      { label: 'Tickets', value: (s.tickets || 0).toLocaleString('en-IN'), icon: MdSupportAgent, gradient: 'linear-gradient(135deg,#6366f1,#8b5cf6)', sub: `${s.orderRelatedPct ?? 0}% about an order`, onClick: () => goTo('tickets'), active: isActive('tickets') },
      { label: 'Open', value: (s.open || 0).toLocaleString('en-IN'), icon: MdMarkEmailUnread, gradient: 'linear-gradient(135deg,#f59e0b,#d97706)', sub: 'Not picked up yet', onClick: () => goTo('tickets', { status: 'open' }), active: isActive('tickets', { status: 'open' }) },
      { label: 'In Progress', value: (s.inProgress || 0).toLocaleString('en-IN'), icon: MdAutorenew, gradient: 'linear-gradient(135deg,#0ea5e9,#2563eb)', sub: 'Being worked on', onClick: () => goTo('tickets', { status: 'in-progress' }), active: isActive('tickets', { status: 'in-progress' }) },
      { label: 'Resolved', value: (s.resolved || 0).toLocaleString('en-IN'), icon: MdCheckCircle, gradient: 'linear-gradient(135deg,#10b981,#059669)', sub: s.resolutionRate === null || s.resolutionRate === undefined ? '—' : `${s.resolutionRate}% resolution rate`, onClick: () => goTo('tickets', { status: 'resolved' }), active: isActive('tickets', { status: 'resolved' }) },
      { label: 'Avg First Response', value: dur(s.avgFirstResponseHours) || '—', icon: MdSpeed, gradient: 'linear-gradient(135deg,#14b8a6,#0d9488)', sub: s.respondedIn24Pct === null || s.respondedIn24Pct === undefined ? 'No replies yet' : `${s.respondedIn24Pct}% answered within 24h` },
      { label: 'Avg Resolution Time', value: dur(s.avgResolutionHours) || '—', icon: MdTimer, gradient: 'linear-gradient(135deg,#a855f7,#7e22ce)', sub: 'Raised → resolved', onClick: () => goTo('subjects'), active: isActive('subjects') },
      { label: 'Waiting on You', value: (s.waiting || 0).toLocaleString('en-IN'), icon: MdHourglassBottom, gradient: 'linear-gradient(135deg,#f43f5e,#dc2626)', sub: `${s.overdue || 0} waiting over 24 hours`, onClick: () => goTo('waiting'), active: isActive('waiting') },
      { label: 'Repeat Contacts', value: (s.repeatCustomers || 0).toLocaleString('en-IN'), icon: MdReceiptLong, gradient: 'linear-gradient(135deg,#0f172a,#334155)', sub: 'Customers with 2+ tickets', onClick: () => goTo('customers'), active: isActive('customers') },
    ]}
    banner={(s, { goTo }) =>
      s.overdue > 0 ? (
        <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => goTo('waiting')}>Reply now</Button>}>
          {s.overdue} ticket(s) have been waiting on your team for more than 24 hours.
        </Alert>
      ) : null
    }
  />
);

export default SupportReport;
